"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SlidersHorizontal, ShieldCheck, MailCheck, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const preferenceItems = [{
  id: "item-1",
  question: "How do I update the topics and industries I receive?",
  answer: "Every edition of the MinfoMedia Executive Briefing includes a direct 'Manage Preferences' link in the footer. Clicking this takes you to your personal subscriber dashboard where you can toggle specific research domains—such as Market Insights, Enterprise AI, or Whitepaper Summaries—on or off without re-entering your details."
}, {
  id: "item-2",
  question: "Can I switch between weekly summaries and monthly digests?",
  answer: "Yes. In your preference settings, you can adjust your dispatch cadence. Choose between our regular weekly analytical brief, a condensed monthly executive digest, or event-driven alerts for flagship research releases."
}, {
  id: "item-3",
  question: "How quickly does an unsubscribe request take effect?",
  answer: "Unsubscribe requests are processed immediately through the one-click link at the bottom of every dispatch. You will receive an immediate on-screen confirmation and no further scheduled mailings will be sent to your address."
}, {
  id: "item-4",
  question: "Will my contact information ever be shared with third parties?",
  answer: "No. MinfoMedia maintains strict editorial independence and institutional privacy standards. Your email address and reading preferences are strictly protected under global data protection frameworks and are never sold, rented, or distributed to advertisers or external partners."
}, {
  id: "item-5",
  question: "What if I need to update my corporate email address?",
  answer: "You can transfer your subscription history and active research report library to a new email address via the preference management page, or simply contact our circulation desk at info@minfomedia.com for assistance."
}];
const pillars = [{
  icon: SlidersHorizontal,
  title: "One-click controls",
  description: "Tailor your research topics and frequency directly from the footer of any email."
}, {
  icon: ShieldCheck,
  title: "Zero third-party sharing",
  description: "Strict editorial firewalls ensure your subscriber data remains confidential."
}, {
  icon: MailCheck,
  title: "Guaranteed unsubscribe",
  description: "Instant one-tap opt-out honored on every single communication we distribute."
}];
export default function Preferences() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="manage-your-preferences" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial Section Header with Broadsheet Masthead Rule */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="max-w-[840px] mx-auto text-center mb-12 sm:mb-16">
          {/* Broad-sheet double rule motif */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-[1px] w-12 bg-border" />
            <div className="h-[2px] w-2 bg-primary rounded-full" />
            <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground font-default">
              Subscriber Autonomy & Privacy
            </span>
            <div className="h-[2px] w-2 bg-primary rounded-full" />
            <div className="h-[1px] w-12 bg-border" />
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-4xl font-semibold tracking-tight font-serif text-foreground mb-4">
            Manage your email preferences
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default max-w-2xl mx-auto leading-relaxed">
            You stay in complete control of what lands in your inbox. Select your focus topics, adjust delivery frequency, or opt out at any moment.
          </p>
        </motion.div>

        {/* 3 Pillar Highlights */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 sm:mb-16 max-w-[1100px] mx-auto">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card border border-border shadow-xs hover:border-primary/30 transition-colors p-6 flex flex-col justify-between">
                  <CardContent className="p-0">
                    <div className="size-10 rounded-sm bg-muted flex items-center justify-center text-foreground mb-4">
                      <IconComponent className="size-5" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground font-default mb-2">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Accordion Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="max-w-[840px] mx-auto">
          <div className="bg-card border border-border rounded-md shadow-xs p-6 sm:p-8">
            <div className="border-b border-border pb-4 mb-4 flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Circulation Standards & FAQs
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                <Sparkles className="size-3.5 text-primary" />
                Updated Weekly
              </span>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {preferenceItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-b border-border/80 last:border-b-0 py-1">
                  <AccordionTrigger className="text-left font-serif text-base sm:text-lg font-semibold text-foreground hover:text-foreground/80 py-4 transition-colors">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 font-default">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Privacy & Editorial Reassurance Footer */}
          <div className="mt-8 pt-6 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className="text-xs sm:text-sm text-muted-foreground">
              Questions regarding our institutional privacy standards? Read our full data protection policy.
            </div>
            <div className="flex items-center gap-3">
              <Button asChild variant="outline" size="sm">
                <Link to="/PrivacyDataProtectionPage" className="inline-flex items-center gap-1.5 text-xs font-medium">
                  Privacy Policy
                  <ArrowRight className="size-3" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}